import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Loader from "../../components/ui/Loader";
import EmptyState from "../../components/ui/EmptyState";
import { useApi } from "../../services/useApi";
import { useAuth } from "../auth/useAuth";
import {
  deleteSessionNote,
  deleteSessionNoteImage,
  getSessionNotes,
  updateSessionNote,
  uploadSessionNoteImage,
} from "./sessionNotesService";
import NoteCard from "../../components/notes/NoteCard";

const TeamSessionNotesPage = () => {
  const { sessionId } = useParams();
  const api = useApi();
  const { token } = useAuth();
  const [notesData, setNotesData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!token || !sessionId) {
        setLoading(false);
        return;
      }
      try {
        const data = await getSessionNotes(api, sessionId, "TEAM");
        if (!mounted) return;
        setNotesData(data);
      } catch (err) {
        if (!mounted) return;
        setNotesData(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [api, token, sessionId]);

  const handleUpdateNote = async (id, description) => {
    await updateSessionNote(api, id, description, false);
    setNotesData((prev) =>
      prev
        ? {
            ...prev,
            notes: prev.notes.map((n) =>
              n.id === id ? { ...n, description } : n,
            ),
          }
        : prev,
    );
  };

  const handleUploadImage = async (id, file) => {
    const uploaded = await uploadSessionNoteImage(api, id, file);
    setNotesData((prev) =>
      prev
        ? {
            ...prev,
            notes: prev.notes.map((n) =>
              n.id === id
                ? { ...n, files: [...(n.files || []), uploaded] }
                : n,
            ),
          }
        : prev,
    );
  };

  const handleDeleteNote = async (id) => {
    await deleteSessionNote(api, id);
    setNotesData((prev) =>
      prev ? { ...prev, notes: prev.notes.filter((n) => n.id !== id) } : prev,
    );
  };

  const handleDeleteImage = async (noteId, imageId, imageUrl) => {
    let prevState;
    setNotesData((prev) => {
      prevState = prev;
      return prev
        ? {
            ...prev,
            notes: prev.notes.map((n) =>
              n.id === noteId
                ? {
                    ...n,
                    files: (n.files || []).filter((f) =>
                      imageId
                        ? f.id !== imageId && f.imageId !== imageId
                        : f.url !== imageUrl,
                    ),
                  }
                : n,
            ),
          }
        : prev;
    });
    try {
      if (!imageId) return;
      await deleteSessionNoteImage(api, noteId, imageId);
    } catch {
      if (prevState) setNotesData(prevState);
    }
  };

  return (
    <div>
      <h2>Team Session Notes</h2>
      {loading && <Loader />}
      {!loading && !notesData && (
        <EmptyState title="No notes" message="No notes found for this session." />
      )}
      {!loading && notesData && (
        <div className="notes-list">
          {(notesData.notes || []).map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              canEdit={note.belongsToCurrentUser}
              onUpdate={handleUpdateNote}
              onUploadImage={handleUploadImage}
              onDelete={handleDeleteNote}
              onDeleteImage={handleDeleteImage}
              modalOnEdit
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamSessionNotesPage;
